import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button, Card, Field, Input, Segmented } from "@/components/kit";

export const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export function usePlans() {
  return useQuery({ queryKey: ["master-plans"], queryFn: () => api("/master/plans") }).data || [];
}

export function PlanSelect({ value, onChange }) {
  const plans = usePlans();
  return (
    <select className="input" value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid="tenant-plan-select">
      <option value="">Sem plano</option>
      {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · R$ {p.price.toFixed(2).replace(".", ",")}/mês{p.active ? "" : " (inativo)"}</option>)}
    </select>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input type="color" className="w-12 h-[46px] rounded-lg border cursor-pointer" style={{ borderColor: "var(--border)" }} value={value || "#4E6E5D"} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-picker`} />
        <Input value={value || ""} placeholder="#4E6E5D" onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </div>
    </Field>
  );
}

function LogoUpload({ onUrl }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      onUrl((await api("/master/logo", { method: "POST", form: fd })).url);
      toast.success("Logotipo enviado");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" hidden onChange={pick} data-testid="brand-logo-file-input" />
      <Button size="sm" variant="secondary" icon={Upload} loading={busy} onClick={() => ref.current.click()} data-testid="brand-logo-upload-button">Enviar arquivo</Button>
    </>
  );
}

export function BrandFields({ brand, setBrand }) {
  const set = (k) => (v) => setBrand({ ...brand, [k]: v });
  return (
    <Card className="flex flex-col gap-4" data-testid="tenant-brand-card">
      <div className="h3">Marca (white-label)</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome exibido no app"><Input value={brand.app_name || ""} onChange={(e) => set("app_name")(e.target.value)} placeholder="Ex.: San Gastro · CMV" data-testid="brand-app-name-input" /></Field>
        <Field label="Frase da tela de login"><Input value={brand.tagline || ""} onChange={(e) => set("tagline")(e.target.value)} placeholder="Gestão de estoque do seu restaurante" data-testid="brand-tagline-input" /></Field>
        <Field label="Logotipo" hint="Envie um PNG, JPG, WEBP ou SVG (até 2 MB) ou cole um link público.">
          <div className="flex gap-2"><Input value={brand.logo_url || ""} onChange={(e) => set("logo_url")(e.target.value)} placeholder="https://…/logo.png" data-testid="brand-logo-input" /><LogoUpload onUrl={set("logo_url")} /></div>
        </Field>
        <div className="flex items-end gap-3">{brand.logo_url ? <><img src={brand.logo_url} alt="" className="h-12 max-w-[160px] object-contain" data-testid="brand-logo-preview" /><button type="button" className="caption underline" onClick={() => set("logo_url")("")} data-testid="brand-logo-remove">Remover</button></> : <span className="caption">Sem logotipo</span>}</div>
        <ColorField label="Cor principal" value={brand.brand} onChange={set("brand")} testid="brand-color-input" />
        <ColorField label="Cor de destaque" value={brand.accent} onChange={set("accent")} testid="brand-accent-input" />
      </div>
    </Card>
  );
}

export function StatusField({ value, onChange, withBlocked }) {
  const opts = [{ value: "teste", label: "Em teste" }, { value: "ativo", label: "Ativo" }, ...(withBlocked ? [{ value: "bloqueado", label: "Bloqueado" }] : [])];
  return <Segmented options={opts} value={value} onChange={onChange} testid="tenant-status" />;
}
